import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons for bundlers (point to CDN assets)
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const pin = (color) => L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;transform:translate(-13px,-26px)"><svg viewBox="0 0 24 24" width="26" height="26" fill="${color}" stroke="white" stroke-width="1.5"><path d="M12 0C7 0 3 4 3 9c0 6 9 15 9 15s9-9 9-15c0-5-4-9-9-9z"/><circle cx="12" cy="9" r="3.2" fill="white" stroke="none"/></svg></div>`,
  iconSize: [26, 26],
  iconAnchor: [0, 0],
});

const livePin = () => L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;transform:translate(-11px,-11px)"><span style="display:block;width:22px;height:22px;border-radius:50%;background:#1A6AE0;border:3px solid #fff;box-shadow:0 0 0 5px rgba(26,106,224,0.30)"></span></div>`,
  iconSize: [22, 22],
  iconAnchor: [0, 0],
});

export default function RouteMap({ route, loading, live }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const liveRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true }).setView([39.5, -98.35], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    liveRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Static route (completed / selected trip)
  useEffect(() => {
    const map = mapRef.current, group = layerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    map.invalidateSize();
    if (!route) return;
    const pts = [];
    if (route.start) {
      L.marker([route.start.lat, route.start.lon], { icon: pin("#C05621") }).bindPopup(`Start: ${route.start.label}`).addTo(group);
      pts.push([route.start.lat, route.start.lon]);
    }
    if (route.end) {
      L.marker([route.end.lat, route.end.lon], { icon: pin("#1A1A18") }).bindPopup(`End: ${route.end.label}`).addTo(group);
      pts.push([route.end.lat, route.end.lon]);
    }
    if (route.line && route.line.length > 1) {
      L.polyline(route.line, { color: "#C05621", weight: 5, opacity: 0.85 }).addTo(group);
    } else if (pts.length === 2) {
      L.polyline(pts, { color: "#C05621", weight: 4, opacity: 0.75, dashArray: "8 6" }).addTo(group);
    }
    const bounds = route.line && route.line.length > 1 ? L.latLngBounds(route.line) : (pts.length ? L.latLngBounds(pts) : null);
    if (bounds) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    else if (pts.length === 1) map.setView(pts[0], 12);
  }, [route]);

  // Live GPS tracking
  useEffect(() => {
    const map = mapRef.current, group = liveRef.current;
    if (!map || !group) return;
    group.clearLayers();
    if (!live || !live.current) return;
    map.invalidateSize();
    // Build the driven trail and always connect it to the current position
    const line = Array.isArray(live.path) ? [...live.path] : [];
    const cur = live.current;
    const lastPt = line[line.length - 1];
    if (cur && (!lastPt || lastPt[0] !== cur[0] || lastPt[1] !== cur[1])) line.push(cur);
    if (line.length > 1) {
      L.polyline(line, { color: "#1A6AE0", weight: 5, opacity: 0.9, lineJoin: "round" }).addTo(group);
    }
    if (line.length > 0) {
      L.circleMarker(line[0], { radius: 6, color: "#ffffff", weight: 2, fillColor: "#16a34a", fillOpacity: 1 }).bindPopup("Start").addTo(group);
    }
    L.marker(cur, { icon: livePin() }).addTo(group);
    map.setView(cur, Math.max(map.getZoom(), 16), { animate: true });
  }, [live]);

  return (
    <div className="relative w-full h-full">
      <div ref={elRef} data-testid="route-map" className="w-full h-full" style={{ minHeight: 420, zIndex: 0 }} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-[500] pointer-events-none" data-testid="route-map-loading">
          <span className="text-sm text-[var(--text-2)] bg-white px-3 py-1.5 rounded shadow">Finding route…</span>
        </div>
      )}
      {!route && !live && !loading && (
        <div className="absolute inset-0 flex items-center justify-center z-[400] pointer-events-none px-6">
          <p className="text-sm text-[var(--text-2)] bg-white/90 px-4 py-2 rounded text-center max-w-xs">Start a live trip, add a trip with locations, or tap a trip below to see its route.</p>
        </div>
      )}
    </div>
  );
}
